import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import TestReportService, {
  BatchOptionItem,
  TestReportDTO,
  TestReportResultDTO,
  UpsertRequest,
  MUUserItem,
} from "../../services/TestReport/TestReport.service";
import productBatchService from "../../services/productbatch/productBatchService";

import { generateTestReportPdf } from "./TestReportPdf";
import qaSeal from "../../assests/images/badge.png";
import centerLogo from "../../assests/images/DesiriLogo.png";

const formatDateOnly = (d?: Date | string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB") : "";

const fmtDynamic = (n: number) => {
  if (n == null) return "—";
  const s = n.toString();
  if (!s.includes(".")) return s;
  return s.replace(/\.0+$/, "").replace(/(\.[1-9]*)0+$/, "$1");
};

const normalizeStatus = (s?: string | null) => {
  const t = (s ?? "").trim().toUpperCase();
  return t || "—";
};

const parseNum = (val: unknown): number | null => {
  const t = (val ?? "").toString().trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const normalizeText = (value?: string | null) =>
  (value ?? "").toString().trim().toLowerCase();

const isGheeLabel = (value?: string | null) => normalizeText(value).includes("ghee");

const cleanDecimal = (s: string) =>
  (s ?? "").toString().replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

const sanitizeBatchNo = (value: string) => value.replace(/[^0-9a-z]/gi, "").toUpperCase();

const simplifyOilName = (oilName?: string | null): string => {
  if (!oilName) return "—";
  const raw = oilName.trim();
  const up = raw.toUpperCase();
  const cleaned = raw.replace(/[()]/g, "").replace(/[,;]+/g, " ").trim();

    if (up.includes("DESI GHEE") || up.includes("GHEE"))
    return "Desi Ghee";


  if (up.includes("ALMOND")) return "Almond Oil";
  if (up.includes("COCONUT")) return "Coconut Oil";
  if (up.includes("GROUNDNUT") || up.includes("PEANUT")) return "Groundnut Oil";
  if (up.includes("CASTOR")) return "Castor Oil";
  if (up.includes("SESAME")) return "Sesame Oil";
  if (up.includes("SUNFLOWER")) return "Sunflower Oil";
  if (up.includes("RICE BRAN") || up.includes("RICEBRAN")) return "Rice Bran Oil";
  if (up.includes("OLIVE")) return "Olive Oil";
  if (up.includes("FLAXSEED") || up.includes("LINSEED")) return "Flaxseed Oil";
  if (up.includes("MUSTARD")) return "Mustard Oil";
  if (up.includes("NIGER")) return "Niger Oil";
  if (up.includes("SAFFLOWER")) return "Safflower Oil";

  if (/\bOIL\b/i.test(cleaned)) {
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    if (tokens.length && tokens[tokens.length - 1].toUpperCase() === "OIL") tokens.pop();
    if (tokens.length) {
      const last = tokens[tokens.length - 1];
      if (/^[A-Za-z]+$/.test(last)) {
        return `${last.charAt(0).toUpperCase() + last.slice(1).toLowerCase()} Oil`;
      }
    }
    return cleaned;
  }

  {
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      const last = tokens[tokens.length - 1];
      if (/^[A-Za-z]+$/.test(last)) {
        return `${last.charAt(0).toUpperCase() + last.slice(1).toLowerCase()} Oil`;
      }
    }
  }

  return raw.length > 40 ? raw.slice(0, 37) + "..." : raw;
};

const CARD_BG = "#f8f9fa";
const CARD_STYLE: React.CSSProperties = {
  border: "1px solid #e6edf3",
  borderRadius: 10,
  padding: "12px",
  marginBottom: 12,
  background: CARD_BG,
  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
};

const HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const TITLE_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontSize: "1.05rem",
  margin: 0,
  color: "#3c3a3aff",
};

const META_LABEL_STYLE: React.CSSProperties = {
  fontSize: 13,
  color: "#145326",
  marginBottom: 6,
};

const META_VALUE_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontSize: 16,
  color: "#145326",
};

const SAMPLE_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 18,
  padding: "12px 6px",
  background: "#ffffff",
};

const SAMPLE_LABEL_STYLE: React.CSSProperties = {
  minWidth: 340,
  color: "#374151",
  fontSize: 14,
};

const INPUT_STYLE: React.CSSProperties = {
  width: 140,
  height: 36,
  padding: "6px 10px",
  textAlign: "right",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  background: "rgba(239, 236, 236, 1)",
};

const DROPDOWN_BUTTON_STYLE: React.CSSProperties = {
  border: "1px solid #0a9130",
  borderRadius: 6,
  width: 40,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#ffffff",
};

type ToleranceRow = {
  testId: number;
  minTolerance: number | null;
  maxTolerance: number | null;
};

type FormErrors = {
  product?: string;
  batch?: string;
  tests?: string;
  mu?: string;
  tester?: string;
  testedDate?: string;
  sampleQty?: string;
  analysisStart?: string;
  analysisEnd?: string;
  times?: string;
  duplicate?: string;
};

const TestReportGenerate: React.FC = () => {
  const [reportDate] = useState<Date>(new Date());
  const [manufacturingUnitId, setManufacturingUnitId] = useState<number | null>(null);
  const [manufacturingUnitName, setManufacturingUnitName] = useState<string>("");
  const [testedByOptions, setTestedByOptions] = useState<MUUserItem[]>([]);
  const [testedById, setTestedById] = useState<number | null>(null);

  const [testedDate, setTestedDate] = useState<Date | null>(new Date());
  const [sampleQty, setSampleQty] = useState<number | null>(null);

  const [analysisStart, setAnalysisStart] = useState<Date | null>(null);
  const [analysisEnd, setAnalysisEnd] = useState<Date | null>(null);

  const [oilTypes, setOilTypes] = useState<any[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [batchNo, setBatchNo] = useState<string>("");

  const [report, setReport] = useState<TestReportDTO | null>(null);
  const [reportId, setReportId] = useState<number | null>(null);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [calculatedResults, setCalculatedResults] = useState<TestReportResultDTO[]>([]);

  const [savingFinal, setSavingFinal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [autoCalcLoading, setAutoCalcLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showValidation, setShowValidation] = useState(false);

  const [batchOptions, setBatchOptions] = useState<BatchOptionItem[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [batchLookupBusy, setBatchLookupBusy] = useState(false);

  const [oilLockedByBatch, setOilLockedByBatch] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const dupeToastShownRef = useRef(false);

  const normalizeBatchCode = (value: string) => value.trim().toUpperCase().replace(/[^0-9A-Z]/g, "");
  const extractBatchDigits = (value: string) => value.replace(/\D/g, "");

  const resolveBatchFromSelection = useCallback(
    (batchId: number | null, batchNumber: string | null) => {
      if (batchId != null) {
        return batchOptions.find((b) => Number(b.batchId) === Number(batchId)) ?? null;
      }

      const normalized = normalizeBatchCode(batchNumber ?? "");
      const digits = extractBatchDigits(batchNumber ?? "");

      if (normalized) {
        const byBatchCode = batchOptions.find((b) => normalizeBatchCode(String(b.batchCode ?? "")) === normalized);
        if (byBatchCode) return byBatchCode;
      }

      if (digits) {
        const byBatchId = batchOptions.find((b) => String(b.batchId ?? "") === digits);
        if (byBatchId) return byBatchId;

        const byBatchCode = batchOptions.find((b) => extractBatchDigits(String(b.batchCode ?? "")) === digits);
        if (byBatchCode) return byBatchCode;
      }

      return null;
    },
    [batchOptions]
  );

  const selectedProduct = useMemo(
    () => oilTypes.find((oil) => Number(oil.id) === Number(selectedProductId)) ?? null,
    [oilTypes, selectedProductId]
  );

  const selectedProductSubCategoryId = selectedProduct
    ? Number(
        selectedProduct.subCategoryId ??
          selectedProduct.subcategoryId ??
          selectedProduct.SubCategoryId ??
          0
      ) || null
    : null;

  const selectedProductIsGhee = Boolean(
    selectedProduct &&
      (isGheeLabel(selectedProduct.subCategoryName) ||
        isGheeLabel(selectedProduct.subcategoryName) ||
        isGheeLabel(selectedProduct.originalName) ||
        isGheeLabel(selectedProduct.name))
  );

  const filteredBatchOptions = useMemo(() => {
    if (!selectedProductId) return batchOptions;

    const selectedId = Number(selectedProductId);
    return batchOptions.filter((b) => {
      const batchProductId = Number(b.byProductId ?? b.productId ?? 0);
      if (batchProductId === selectedId) return true;

      if (!selectedProductIsGhee) return false;

      const batchSubCategoryId = Number(b.subCategoryId ?? 0) || null;
      const batchSubCategoryName = normalizeText(b.subCategoryName);

      if (selectedProductSubCategoryId && batchSubCategoryId) {
        return batchSubCategoryId === selectedProductSubCategoryId;
      }

      return batchSubCategoryName.includes("ghee");
    });
  }, [batchOptions, selectedProductId, selectedProductIsGhee, selectedProductSubCategoryId]);

  // Load batches on mount
  useEffect(() => {
    (async () => {
      try {
        const batches = await TestReportService.getBatches("");
        setBatchOptions(batches ?? []);
      } catch (err) {
        console.error("Failed to load batches", err);
        setBatchOptions([]);
      }
    })();
  }, []);

  // ✅ FIX: Updated handleBatchSelect to trigger calculations
  const handleBatchSelect = (batchId: number | null) => {
    setSelectedBatchId(batchId);
    setReportId(null);
    setBatchNo("");

    if (!batchId) {
      setOilLockedByBatch(false);
      setSelectedProductId(null);
      setManufacturingUnitId(null);
      setManufacturingUnitName("");
      return;
    }

    const batch = batchOptions.find(b => b.batchId === batchId);
    if (!batch) return;

    setManufacturingUnitId(batch.manufacturingUnitId ?? batch.posId ?? null);
    setManufacturingUnitName(batch.manufacturingUnitName ?? "");

    // Auto-select oil
    if (batch.byProductId) {
      setSelectedProductId(batch.byProductId);
      setOilLockedByBatch(true);
    }
    
    // ✅ Trigger calculation if inputs exist
    if (inputValues && Object.keys(inputValues).length > 0) {
      setTimeout(() => triggerPreview(inputValues), 100);
    }
  };

  const effectiveBatchNo = useMemo(() => {
    if (batchNo.trim()) return batchNo.trim();

    if (selectedBatchId) {
      const batch = batchOptions.find(b => b.batchId === selectedBatchId);
      const code = batch?.batchCode ?? String(batch?.batchId ?? "");
      return code ? code : null;
    }

    return null;
  }, [batchNo, selectedBatchId, batchOptions]);

  const [tolerances, setTolerances] =
    useState<Map<number, { min: number | null; max: number | null }>>(() => new Map());

  const [expandedTests, setExpandedTests] = useState<Set<number>>(new Set());
  const testRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const [dupeChecking, setDupeChecking] = useState(false);
  const [dupeExists, setDupeExists] = useState(false);
  const [dupeMsg, setDupeMsg] = useState<string | null>(null);

  const canShowInputCard = Boolean(selectedProductId && effectiveBatchNo && report && !dupeExists);

  const headerRefs = {
    product: useRef<HTMLDivElement>(null),
    batch: useRef<HTMLDivElement>(null),
    tester: useRef<HTMLDivElement>(null),
    testedDate: useRef<HTMLDivElement>(null),
    analysisStart: useRef<HTMLDivElement>(null),
    analysisEnd: useRef<HTMLDivElement>(null),
    sampleQty: useRef<HTMLDivElement>(null),
  } as const;
  type HeaderKey = keyof typeof headerRefs;

  const toast = useRef<Toast>(null);
  const show = (sev: "success" | "info" | "warn" | "error", title: string, msg: string) =>
    toast.current?.show({ severity: sev, summary: title, detail: msg, life: 2200 });

  const scrollAndFocus = (el?: HTMLElement | null) => {
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => (el.querySelector("input, button, textarea, [tabindex]") as HTMLElement | null)?.focus?.(), 120);
  };

  const getMuName = () => manufacturingUnitName || "—";
  const getTesterName = () =>
    testedByOptions.find((o) => o.id === testedById)?.name ?? "—";

  // Lookups
  useEffect(() => {
    (async () => {
      setLoadingLookups(true);
      try {
        const [oilsRaw, testers] = await Promise.all([
          TestReportService.getOilTypes(),
          TestReportService.getOilTesters(),
        ]);

        const oilsMapped = (oilsRaw ?? []).map((o: any) => {
          const original = o?.name ?? o?.label ?? o?.Name ?? "";
          return {
            ...(o ?? {}),
            id: o?.id ?? o?.value ?? o?.Id,
            name: simplifyOilName(original),
            originalName: original,
            subCategoryId: o?.subCategoryId ?? o?.subCategoryID ?? o?.SubCategoryId ?? null,
            subCategoryName: o?.subCategoryName ?? o?.SubCategoryName ?? null,
          };
        });

        setOilTypes(oilsMapped);
        setTestedByOptions(Array.isArray(testers) ? testers : []);
      } catch (err) {
        console.error("Load failed", err);
        show("error", "Load failed", "Could not load lookups");
        setOilTypes([]);
        setTestedByOptions([]);
      } finally {
        setLoadingLookups(false);
      }
    })();
  }, []);

  useEffect(() => {
    let alive = true;

    const resolve = async () => {
      const batch = resolveBatchFromSelection(selectedBatchId, batchNo);

      if (!batch) {
        if (!alive) return;
        setManufacturingUnitId(null);
        setManufacturingUnitName("");
        return;
      }

      const candidateMuId = Number(batch.manufacturingUnitId ?? batch.posId ?? 0);
      const candidateMuName = String(batch.manufacturingUnitName ?? "").trim();

      if (Number.isFinite(candidateMuId) && candidateMuId > 0 && candidateMuName) {
        if (!alive) return;
        setManufacturingUnitId(candidateMuId);
        setManufacturingUnitName(candidateMuName);
        return;
      }

      if (!Number.isFinite(candidateMuId) || candidateMuId <= 0) {
        const batchKey = Number(batch.batchId ?? 0);
        if (batchKey > 0) {
          setBatchLookupBusy(true);
          try {
            const details = await productBatchService.getById(batchKey);
            if (!alive) return;

            const detailMuId = Number(details?.posId ?? 0);
            const detailMuName = String(details?.manufacturingUnitAddress ?? "").trim();

            setManufacturingUnitId(Number.isFinite(detailMuId) && detailMuId > 0 ? detailMuId : null);
            setManufacturingUnitName(detailMuName);
          } catch (err) {
            console.error("Failed to resolve manufacturing unit from batch", err);
            if (alive) {
              setManufacturingUnitId(null);
              setManufacturingUnitName("");
            }
          } finally {
            if (alive) setBatchLookupBusy(false);
          }
        }
      }
    };

    resolve();
    return () => {
      alive = false;
    };
  }, [selectedBatchId, batchNo, resolveBatchFromSelection]);

  const loadTolerances = async (productId: number) => {
    try {
      const rows: ToleranceRow[] = await TestReportService.getTolerances(productId);
      const map = new Map<number, { min: number | null; max: number | null }>();
      (rows ?? []).forEach((r) => map.set(r.testId, { min: r.minTolerance, max: r.maxTolerance }));
      setTolerances(map);
    } catch {
      setTolerances(new Map());
    }
  };

  // ✅ FIX: Updated triggerPreview function
  const triggerPreview = (values?: Record<string, string>) => {
    if (!selectedProductId) return;
    if (autoCalcLoading) return;
    
    setAutoCalcLoading(true);

    const obj: Record<string, number> = {};
    Object.entries(values ?? inputValues).forEach(([k, v]) => {
      const num = parseNum(v);
      if (num !== null) obj[k] = num;
    });

    TestReportService.calculateResultsPreviewForProduct(selectedProductId, obj)
      .then((res) => setCalculatedResults(res ?? []))
      .catch(() => {})
      .finally(() => setAutoCalcLoading(false));
  };

  // Load template + tolerances on product change
  useEffect(() => {
    (async () => {
      setShowValidation(false);
      setFormErrors({});
      setReportId(null);
      setCalculatedResults([]);
      setInputValues({});
      setReport(null);

      if (!selectedProductId) return;

      try {
        const tpl = await TestReportService.getTemplateForProduct(selectedProductId);
        setReport(tpl);
        await loadTolerances(selectedProductId);

        const initial: Record<string, string> = {};
        for (const sample of tpl?.samples ?? []) {
          for (const r of sample.results ?? []) {
            if (r.value != null) initial[`${r.testId}:${sample.sampleId}`] = String(r.value);
          }
        }
        setInputValues(initial);
        triggerPreview(initial);
      } catch (err) {
        console.error("Failed to load template", err);
      }
    })();
  }, [selectedProductId]);

  // ✅ FIX: Added useEffect to recalculate when effectiveBatchNo changes
  useEffect(() => {
    if (selectedProductId && effectiveBatchNo && Object.keys(inputValues).length > 0) {
      triggerPreview(inputValues);
    }
  }, [effectiveBatchNo]);

  const tests = useMemo(() => {
    if (!report) return [];
    const map: Record<
      number,
      { testId: number; testName: string; rows: Array<{ sampleId: number; sampleName: string }>; keys: string[] }
    > = {};
    for (const sample of report.samples ?? []) {
      for (const r of sample.results ?? []) {
        if (!map[r.testId]) {
          map[r.testId] = { testId: r.testId, testName: r.testName, rows: [], keys: [] };
        }
        map[r.testId].rows.push({ sampleId: sample.sampleId, sampleName: sample.sampleName });
        map[r.testId].keys.push(`${r.testId}:${sample.sampleId}`);
      }
    }
    return Object.values(map).sort((a, b) => a.testName.localeCompare(b.testName));
  }, [report]);

  const completeByTestId = useMemo(() => {
    const m = new Map<number, boolean>();
    tests.forEach((t) => {
      const allFilled = t.keys.every((k) => parseNum(inputValues[k]) !== null);
      m.set(t.testId, allFilled);
    });
    return m;
  }, [tests, inputValues]);

  const missingCells = useMemo(() => {
    if (!report) return [] as { key: string; testId: number; sampleId: number }[];
    const miss: { key: string; testId: number; sampleId: number }[] = [];
    for (const sample of report.samples) {
      for (const r of sample.results) {
        const key = `${r.testId}:${sample.sampleId}`;
        if (parseNum(inputValues[key]) === null) miss.push({ key, testId: r.testId, sampleId: sample.sampleId });
      }
    }
    return miss;
  }, [report, inputValues]);

  useEffect(() => {
    if (!showValidation) return;
    setFormErrors((prev) => {
      const next = { ...prev };
      if (analysisStart) delete next.analysisStart;
      if (analysisEnd) delete next.analysisEnd;
      if (analysisStart && analysisEnd && analysisEnd >= analysisStart) delete next.times;
      return next;
    });
  }, [analysisStart, analysisEnd, showValidation]);

  useEffect(() => {
    if (analysisStart && analysisEnd && analysisEnd <= analysisStart) {
      setAnalysisEnd(null);
      show("info", "End time cleared", "Start moved after End. Please select a new end time.");
    }
  }, [analysisStart]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setDupeChecking(true);
      setDupeExists(false);
      setDupeMsg(null);

      if (!selectedProductId || !effectiveBatchNo) {
        setDupeChecking(false);
        return;
      }

      try {
        await new Promise((r) => setTimeout(r, 250));

        const exists = await TestReportService.existsReportForProductBatch(
          selectedProductId,
          effectiveBatchNo,
          reportId ?? null
        );

        if (cancelled) return;

        const msg = exists ? "This batch number is already used for the selected oil. Please use a different batch or pick a different oil." : null;

        setDupeExists(exists);
        setDupeMsg(msg);

        if (exists && msg && !dupeToastShownRef.current) {
          show("warn", "Duplicate Batch", msg);
          dupeToastShownRef.current = true;
        }

        setFormErrors((prev) => {
          const next = { ...prev };
          if (exists && msg) next.duplicate = msg;
          else delete next.duplicate;
          return next;
        });
      } catch {
        if (!cancelled) {
          setDupeExists(false);
          setDupeMsg(null);
        }
      } finally {
        if (!cancelled) setDupeChecking(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [selectedProductId, effectiveBatchNo, reportId]);

  useEffect(() => {
    dupeToastShownRef.current = false;
  }, [selectedProductId, batchNo, selectedBatchId]);

  const validateBeforeSave = async (): Promise<boolean> => {
    const errors: FormErrors = {};
    if (!selectedProductId) errors.product = "Please select Oil Type";
    
    if (!batchNo && !selectedBatchId) {
      errors.batch = "Please select Batch ID or enter Batch No";
    }

    if (batchNo && selectedBatchId) {
      show(
        "warn",
        "Invalid Batch Selection",
        "Either select Batch ID or manually enter Batch No (not both)"
      );
      errors.batch = "Either select Batch ID or manually enter Batch No (not both)";
    }

    if (!manufacturingUnitId) errors.batch = "Please select a valid Batch";
    if (!testedById) errors.tester = "Please select Tested By";
    if (!testedDate) errors.testedDate = "Please choose Tested Date";
    if (testedDate) {
      const testedDay = new Date(testedDate);
      const reportDay = new Date(reportDate);
      testedDay.setHours(0, 0, 0, 0);
      reportDay.setHours(0, 0, 0, 0);
      if (testedDay > reportDay) {
        errors.testedDate = "Tested Date cannot be after the Report Date";
      }
    }

    if (!analysisStart) errors.analysisStart = "Please set Analysis Start time";
    if (!analysisEnd) errors.analysisEnd = "Please set Analysis End time";
    if (analysisStart && analysisEnd && analysisEnd < analysisStart) {
      errors.times = "Analysis End cannot be before Analysis Start";
    }

    if (sampleQty == null || !Number.isInteger(sampleQty) || sampleQty <= 0) {
      errors.sampleQty = "Enter a valid Sample Quantity";
    }
    if (missingCells.length > 0) errors.tests = "Please enter values for all tests";

    if (selectedProductId && effectiveBatchNo) {
      const exists = await TestReportService.existsReportForProductBatch(
        selectedProductId,
        effectiveBatchNo,
        reportId ?? null
      );
      if (exists) {
        errors.duplicate = "This batch number is already used for the selected oil. Please use a different batch or pick a different oil.";
      }
    }

    setFormErrors(errors);
    setShowValidation(true);

    const order: Array<HeaderKey | "times" | "tests" | "duplicate"> = [
      "product",
      "batch",
      "tester",
      "testedDate",
      "sampleQty",
      "analysisStart",
      "analysisEnd",
      "times",
      "duplicate",
      "tests",
    ];
    for (const k of order) {
      if (k === "tests" && errors.tests) {
        const first = missingCells[0];
        setExpandedTests((prev) => new Set(prev).add(first.testId));
        setTimeout(() => scrollAndFocus(testRefs.current[first.testId]), 150);
        break;
      } else if (k === "times" && errors.times) {
        scrollAndFocus(headerRefs.analysisStart.current);
        break;
      } else if (k === "duplicate" && errors.duplicate) {
        scrollAndFocus(headerRefs.batch.current);
        break;
      } else if (k !== "tests" && k !== "times" && (errors as any)[k]) {
        scrollAndFocus(headerRefs[k as HeaderKey].current);
        break;
      }
    }

    return Object.keys(errors).length === 0;
  };

  const onChangeResult = (testId: number, sampleId: number, v: string) => {
    const key = `${testId}:${sampleId}`;
    setInputValues((prev) => {
      const next = { ...prev, [key]: v };
      triggerPreview(next);
      return next;
    });

    setIsDirty(true);

    if (showValidation && parseNum(v) !== null) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next.tests;
        return next;
      });
    }
  };

  const handleSaveFinal = async () => {
    const ok = await validateBeforeSave();
    if (!ok) return;

    setSavingFinal(true);
    try {
      const inputsObj: Record<string, number> = {};
      Object.entries(inputValues).forEach(([k, v]) => {
        const n = parseNum(v);
        if (n !== null) inputsObj[k] = n;
      });

      const body: UpsertRequest = {
        reportId,
        productId: selectedProductId!,
        batchId: selectedBatchId ?? null,
        batchNo: batchNo || null,
        sampleQty,
        testedDate,
        analysisStart,
        analysisEnd,
        inputs: inputsObj,
        manufacturingUnitId,
        testedByUserId: testedById,
      };

      const resp = await TestReportService.upsertReportWithResults(body);
      setReport(resp.report);
      setReportId(resp.report.id);
      
      if (!selectedBatchId) {
        const nextBatchNo = String(resp.report.batchNo ?? "").trim();
        setBatchNo(nextBatchNo);
      }

      setCalculatedResults(resp.results ?? []);

      setManufacturingUnitId(resp.report.manufacturingUnitId ?? manufacturingUnitId ?? null);
      setTestedById(resp.report.testedByUserId ?? testedById ?? null);
      setAnalysisStart(resp.report.analysisStartDate ? new Date(resp.report.analysisStartDate) : analysisStart);
      setAnalysisEnd(resp.report.analysisEndDate ? new Date(resp.report.analysisEndDate) : analysisEnd);
      setSampleQty(resp.report.sampleQty ?? sampleQty ?? null);

      setShowValidation(false);
      setFormErrors({});
      show("success", "Saved", "Saved successfully");
      setIsDirty(false);
    } catch (err: any) {
      console.error("UPSERT ERROR", err?.response?.data ?? err);
      show("error", "Error", "Save failed");
    } finally {
      setSavingFinal(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!report || !reportId) {
      show("warn", "Save first", "Please save the report before generating the PDF.");
      return;
    }

    if (missingCells.length > 0) {
      setShowValidation(true);
      setFormErrors((prev) => ({ ...prev, tests: "Please enter values for all tests" }));
      const first = missingCells[0];
      setExpandedTests((prev) => new Set(prev).add(first.testId));
      setTimeout(() => scrollAndFocus(testRefs.current[first.testId]), 120);
      return;
    }

    setPdfLoading(true);
    try {
      if (!calculatedResults || calculatedResults.length === 0) {
        const obj: Record<string, number> = {};
        Object.entries(inputValues).forEach(([k, v]) => {
          const n = Number(v);
          if (Number.isFinite(n)) obj[k] = n;
        });
        const preview = await TestReportService.calculateResultsPreviewForProduct(
          report.productId,
          obj
        );
        setCalculatedResults(preview ?? []);
      }

      const meta = {
        reportId: report.id,
        reportDate,
        manufacturingUnitId: manufacturingUnitId,
        manufacturingUnitName: getMuName(),
        testedDate,
        analysisStart,
        analysisEnd,
        sampleQty: sampleQty ?? null,
        sampleQtyUnit: "ml",
        testerName: getTesterName(),
        oilName: report.productName,
        batchName: report.batchNo != null ? `B${String(report.batchNo).padStart(8, "0")}` : "",
      };

      await generateTestReportPdf({
        meta,
        results: calculatedResults,
        tolerances,
        centerLogoUrl: centerLogo,
        qaSealUrl: qaSeal,
        filename: `TestReport_${report.batchNo}_${report.productName}.pdf`,
      });

      show("success", "PDF ready", "Your test report PDF has been generated.");
    } catch (err) {
      console.error("PDF ERROR", err);
      show("error", "PDF error", "Could not generate PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const rangeText = (testId: number) => {
    const tol = tolerances.get(testId);
    if (!tol) return "—";
    const { min, max } = tol;
    if (min != null && max != null) return `${fmtDynamic(min)} – ${fmtDynamic(max)}`;
    if (min != null) return `≥ ${fmtDynamic(min)}`;
    if (max != null) return `≤ ${fmtDynamic(max)}`;
    return "N.S.";
  };

  const toggleExpand = (testId: number) => {
    setExpandedTests((prev) => {
      const next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <Card title="Generate Oil Test Report" className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div style={{ maxWidth: 170 }}>
            <label className="block text-sm font-medium mb-1">
              Report Date <span className="text-red-600">*</span>
            </label>
            <InputText value={formatDateOnly(reportDate)} readOnly className="w-full" />
          </div>

          <div ref={headerRefs.tester}>
            <label className="block text-sm font-medium mb-1">
              Tested By <span className="text-red-600">*</span>
            </label>
            <Dropdown
              value={testedById}
              options={testedByOptions}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setTestedById(e.value)}
              placeholder={testedByOptions.length === 0 ? "No testers available" : "Select tester"}
              className={`w-full ${showValidation && formErrors.tester ? "p-invalid" : ""}`}
              disabled={testedByOptions.length === 0}
              showClear
            />
          </div>

          <div style={{ maxWidth: 170 }} ref={headerRefs.testedDate}>
            <label className="block text-sm font-medium mb-1">
              Tested Date <span className="text-red-600">*</span>
            </label>
            <Calendar
              readOnlyInput
              value={testedDate}
              maxDate={reportDate}
              onChange={(e) => {
                const selected = e.value as Date | null;
                if (!selected) {
                  setTestedDate(null);
                  return;
                }

                const selectedDay = new Date(selected);
                const reportDay = new Date(reportDate);
                selectedDay.setHours(0, 0, 0, 0);
                reportDay.setHours(0, 0, 0, 0);

                if (selectedDay > reportDay) {
                  show("warn", "Invalid Tested Date", "Tested Date cannot be after the Report Date.");
                  return;
                }

                setTestedDate(selected);
              }}
              dateFormat="dd/mm/yy"
              showIcon
              className={showValidation && formErrors.testedDate ? "p-invalid" : undefined}
            />
          </div>

          <div style={{ maxWidth: 170 }} ref={headerRefs.analysisStart}>
            <label className="block text-sm font-medium mb-1">
              Analysis Start <span className="text-red-600">*</span>
            </label>
            <Calendar
              readOnlyInput
              value={analysisStart}
              onFocus={() => {
                if (!analysisStart) setAnalysisStart(new Date());
              }}
              onChange={(e) => {
                const selected = e.value as Date;
                if (!selected) return;
                setAnalysisStart(selected);
                if (analysisEnd && analysisEnd <= selected) {
                  setAnalysisEnd(null);
                  show("info", "End time cleared", "Start moved after End. Please select a new end time.");
                }
              }}
              timeOnly
              hourFormat="12"
              stepMinute={1}
              showIcon
              icon="pi pi-clock"
              className={
                showValidation && (formErrors.analysisStart || formErrors.times) ? "p-invalid" : undefined
              }
            />
          </div>

          <div style={{ maxWidth: 170 }} ref={headerRefs.analysisEnd}>
            <label className="block text-sm font-medium mb-1">
              Analysis End <span className="text-red-600">*</span>
            </label>
            <Calendar
              readOnlyInput
              value={analysisEnd}
              onChange={(e) => {
                const selected = e.value as Date;
                if (!selected) {
                  setAnalysisEnd(null);
                  return;
                }
                if (analysisStart && selected < analysisStart) {
                  show("warn", "Invalid time", "End time cannot be earlier than start time.");
                  return;
                }
                setAnalysisEnd(selected);
              }}
              timeOnly
              hourFormat="12"
              stepMinute={1}
              showIcon
              icon="pi pi-clock"
              minDate={analysisStart ?? undefined}
              className={
                showValidation && (formErrors.analysisEnd || formErrors.times) ? "p-invalid" : undefined
              }
            />
          </div>

          <div style={{ maxWidth: 180 }} ref={headerRefs.sampleQty}>
            <label className="block text-sm font-medium mb-1">
              Sample Quantity <span className="text-red-600">*</span>
            </label>
            <div className="p-inputgroup">
              <InputNumber
                value={sampleQty}
                onValueChange={(e) => {
                  const v = typeof e.value === "number" ? Math.max(1, Math.floor(e.value)) : null;
                  setSampleQty(v);
                  if (showValidation) {
                    setFormErrors((prev) => {
                      const n = { ...prev };
                      if (v && Number.isInteger(v) && v > 0) delete n.sampleQty;
                      return n;
                    });
                  }
                }}
                mode="decimal"
                min={1}
                useGrouping={false}
                maxFractionDigits={0}
                className={`w-full ${showValidation && formErrors.sampleQty ? "p-invalid" : ""}`}
              />
              <span className="p-inputgroup-addon">ml</span>
            </div>
            {showValidation && formErrors.sampleQty && (
              <small className="p-error block mt-1">{formErrors.sampleQty}</small>
            )}
          </div>

          <div ref={headerRefs.batch} style={{ order: 2 }}>
            <label className="block text-sm font-medium mb-1">
              Batch No <span className="text-red-600 text-xs ml-1">*</span>
              <span className="text-grey-600 text-xs ml-1">(Select Batch ID OR Enter Batch No)</span>
            </label>

            <div className="flex gap-2">
              <Dropdown
                value={selectedBatchId}
                options={filteredBatchOptions}
                optionLabel="batchCode"
                optionValue="batchId"
                onChange={(e) => handleBatchSelect(e.value)}
                placeholder={
                  selectedProductId && filteredBatchOptions.length === 0
                    ? "No batches for this oil"
                    : "Select batch"
                }
                className="w-40"
                showClear
                filter
              />

              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={batchNo}
                onInput={(e) => {
                  const v = sanitizeBatchNo((e.target as HTMLInputElement).value);
                  (e.target as HTMLInputElement).value = v;

                  setSelectedBatchId(null);
                  setBatchNo(v);
                  setReportId(null);
                }}
                placeholder="Enter"
                className={`border rounded p-2 w-28 ${showValidation && (formErrors.batch || formErrors.duplicate) ? "p-invalid" : ""}`}
              />
            </div>

            <div className="mt-2 text-sm text-700">
              Manufacturing Unit:{" "}
              <span className="font-semibold">
                {batchLookupBusy ? "Resolving..." : manufacturingUnitName || "—"}
              </span>
            </div>

            {showValidation && formErrors.batch && (
              <small className="text-red-600 text-sm mt-1 block">{formErrors.batch}</small>
            )}

            {dupeExists && (
              <small className="text-red-600 text-sm mt-1 block">{dupeMsg}</small>
            )}
          </div>

          <div className="md:col-span-1" ref={headerRefs.product} style={{ order: 1 }}>
            <label className="block text-sm font-medium mb-1">
              Oil Type <span className="text-red-600">*</span>
            </label>
            <Dropdown
              value={selectedProductId}
              options={oilTypes}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setSelectedProductId(e.value)}
              placeholder="Select oil type"
              className={`w-full ${showValidation && formErrors.product ? "p-invalid" : ""}`}
              disabled={loadingLookups || oilLockedByBatch}
              showClear={!oilLockedByBatch}
              filter
              filterBy="name,originalName"
              filterPlaceholder="Search oils..."
              filterMatchMode="contains"
            />
            {showValidation && formErrors.product && (
              <small className="p-error"> {formErrors.product} </small>
            )}
          </div>
        </div>
      </Card>

      {canShowInputCard && report && (
        <Card title={simplifyOilName(report.productName).toUpperCase()} style={{ paddingBottom: 12 }}>
          {showValidation && formErrors.tests && (
            <div className="text-red-600 text-sm mb-2">{formErrors.tests}</div>
          )}

          <div style={{ maxHeight: "62vh", overflowY: "auto", paddingRight: 8 }}>
            {tests.map((t) => {
              const isComplete = completeByTestId.get(t.testId) === true;
              const previewRow = calculatedResults?.find((r) => r.testId === t.testId);
              const isOpen = expandedTests.has(t.testId);

              return (
                <div
                  key={t.testId}
                  ref={(el) => (testRefs.current[t.testId] = el)}
                  style={CARD_STYLE}
                >
                  <div style={HEADER_STYLE}>
                    <div style={{ ...TITLE_STYLE }}>{t.testName}</div>

                    <div style={{ marginLeft: "auto" }}>
                      <Button
                        icon={isOpen ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                        onClick={() => toggleExpand(t.testId)}
                        className="p-button-text"
                        style={DROPDOWN_BUTTON_STYLE}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "12px",
                      background: CARD_BG,
                      borderTop: "1px solid #eef2f6",
                      display: "flex",
                      gap: 24,
                      alignItems: "center",
                    }}
                  >
                    <div style={{ minWidth: 220 }}>
                      <div style={META_LABEL_STYLE}>Result</div>
                      <div style={META_VALUE_STYLE}>
                        {isComplete && previewRow && previewRow.result != null
                          ? Number(previewRow.result).toFixed(4)
                          : isComplete && autoCalcLoading
                            ? "…"
                            : "—"}
                      </div>
                    </div>

                    <div style={{ minWidth: 160 }}>
                      <div style={META_LABEL_STYLE}>Status</div>
                      <div style={{ marginTop: 6 }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "6px 10px",
                            borderRadius: 6,
                            color: "#fff",
                            background:
                              isComplete && previewRow?.status === "PASS"
                                ? "#16a34a"
                                : isComplete && previewRow?.status === "FAIL"
                                  ? "#dc2626"
                                  : "#6b7280",
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        >
                          {isComplete && previewRow ? normalizeStatus(previewRow.status) : "—"}
                        </span>
                      </div>
                    </div>

                    <div style={{ marginLeft: "auto", textAlign: "right", minWidth: 240 }}>
                      <div style={META_LABEL_STYLE}>Acceptable Range</div>
                      <div style={{ fontWeight: 600, color: "#145326" }}>{rangeText(t.testId)}</div>
                    </div>
                  </div>

                  {isOpen && (
                    <div>
                      {(t.rows || []).map((r, idx) => {
                        const key = `${t.testId}:${r.sampleId}`;
                        const currentVal = inputValues[key] ?? "";
                        const isMissing = showValidation && parseNum(currentVal) === null;
                        const showBorder = (t.rows?.length ?? 0) > 1 && idx < (t.rows?.length ?? 0) - 1;
                        const rowStyle = {
                          ...SAMPLE_ROW_STYLE,
                          borderBottom: showBorder ? "1px solid #c2b9b9ff" : "none",
                        };

                        return (
                          <div key={key} style={rowStyle}>
                            <div style={SAMPLE_LABEL_STYLE}>{r.sampleName}</div>

                            <div style={{ marginLeft: "auto" }}>
                              <InputText
                                name={key}
                                value={currentVal}
                                onInput={(e) => {
                                  const raw = (e.target as HTMLInputElement).value;
                                  const cleaned = cleanDecimal(raw);
                                  if (cleaned !== raw) (e.target as HTMLInputElement).value = cleaned;
                                  onChangeResult(t.testId, r.sampleId, cleaned);
                                }}
                                placeholder="0"
                                inputMode="decimal"
                                style={INPUT_STYLE}
                              />
                              {isMissing && (
                                <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>Required</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex items-center gap-3" style={{ justifyContent: "flex-end" }}>
            <Button
              label="Save"
              icon="pi pi-check-circle"
              severity="success"
              onClick={handleSaveFinal}
              loading={savingFinal}
              disabled={dupeExists || dupeChecking || !isDirty}
            />
            <Button
              label="Generate PDF"
              icon="pi pi-file-pdf"
              className="p-button-danger"
              onClick={handleGeneratePdf}
              loading={pdfLoading}
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default TestReportGenerate;
